<template>
  <b-modal
    no-close-on-backdrop
    size="lg"
    hide-footer
    id="new_association"
    modal-class="modern-modal"
    @show="evOnShow"
    @shown="evOnShown"
    @hide="evOnHide"
  >
    <template v-slot:modal-title>
      <div class="modal-title-modern">
        <i class="fas fa-link mr-2"></i>
        Nova Associação
      </div>
    </template>

    <div class="modal-content-modern">
      <form @submit.prevent="evOnSubmit">
        <!-- Form Grid -->
        <div class="form-grid">
          <div class="form-group">
            <label-required-field label="Grupo" />
            <b-select
              ref="autofocus"
              v-model="stock_group_id"
              class="modern-select"
            >
              <b-select-option value="">
                <i class="fas fa-warehouse mr-2"></i>
                Selecione um grupo
              </b-select-option>
              <b-select-option
                v-for="group in available_groups"
                :key="group.id"
                :value="group.id"
              >
                <i class="fas fa-warehouse mr-2"></i>
                {{ group.name }}
              </b-select-option>
            </b-select>
            <small class="form-help">
              <i class="fas fa-info-circle mr-1"></i>
              Local onde os itens serão armazenados
            </small>
          </div>

          <div class="form-group">
            <label-required-field label="Categoria" />
            <b-select v-model="stock_item_category_id" class="modern-select">
              <b-select-option value="">
                <i class="fas fa-tags mr-2"></i>
                Selecione uma categoria
              </b-select-option>
              <b-select-option
                v-for="category in available_categories"
                :key="category.id"
                :value="category.id"
              >
                <i
                  :class="getCategoryIcon(category.stock_item_type_name)"
                  class="mr-2"
                ></i>
                {{ category.name }} ({{ category.stock_item_type_name }})
              </b-select-option>
            </b-select>
            <small class="form-help">
              <i class="fas fa-info-circle mr-1"></i>
              Tipo de item que será armazenado no grupo
            </small>
          </div>
        </div>

        <!-- Preview Section -->
        <div
          class="preview-section"
          v-if="stock_group_id && stock_item_category_id"
        >
          <h4 class="preview-title">
            <i class="fas fa-eye mr-2"></i>
            Prévia da Associação
          </h4>
          <div class="preview-content">
            <div class="association-preview">
              <div class="preview-group">
                <i class="fas fa-warehouse preview-icon"></i>
                <div class="preview-info">
                  <span class="preview-label">Grupo</span>
                  <span class="preview-value">{{
                    getGroupName(stock_group_id)
                  }}</span>
                </div>
              </div>

              <div class="association-arrow">
                <i class="fas fa-arrow-right"></i>
              </div>

              <div class="preview-category">
                <i
                  :class="
                    getCategoryIcon(getCategoryTypeName(stock_item_category_id))
                  "
                  class="preview-icon"
                ></i>
                <div class="preview-info">
                  <span class="preview-label">Categoria</span>
                  <span class="preview-value">{{
                    getCategoryName(stock_item_category_id)
                  }}</span>
                  <span class="preview-type">{{
                    getCategoryTypeName(stock_item_category_id)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rules Section -->
        <div class="rules-section">
          <h4 class="rules-title">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            Regras Importantes
          </h4>
          <div class="rules-list">
            <div class="rule-item">
              <div class="rule-icon restriction">
                <i class="fas fa-times"></i>
              </div>
              <div class="rule-content">
                <strong>Incompatibilidade de tipos</strong>
                <span
                  >Produtos não podem ser associados a locais de medicamentos e
                  vice-versa</span
                >
              </div>
            </div>
            <div class="rule-item">
              <div class="rule-icon unique">
                <i class="fas fa-exclamation-circle"></i>
              </div>
              <div class="rule-content">
                <strong>Associação única</strong>
                <span
                  >Cada combinação grupo-categoria pode existir apenas uma
                  vez</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <warning-required-fields />
          <errors-messages :errors="errors" />

          <div class="form-actions">
            <v-btn :disabled="loading" type="submit" class="btn-save-modern">
              <i class="fas fa-link mr-2"></i>
              {{ loading ? "Associando..." : "Criar Associação" }}
            </v-btn>
          </div>
        </div>
      </form>
    </div>
  </b-modal>
</template>

<script>
import toast from "../../../lib/ToastNotification";
import api from "../../../services/api";
import WarningRequiredFields from "../../Components/WarningRequiredFields.vue";
import ErrorsMessages from "../../Components/ErrorsMessages.vue";
import LabelRequiredField from "../../Components/LabelRequiredField.vue";

export default {
  name: "NewAssociation",

  components: {
    WarningRequiredFields,
    ErrorsMessages,
    LabelRequiredField,
  },

  props: {
    associations: { type: Array, required: true },
    groups: { type: Array, required: true },
    categories: { type: Array, required: true },
  },

  data() {
    return {
      errors: [],
      loading: false,

      stock_group_id: "",
      stock_item_category_id: "",
    };
  },

  computed: {
    available_groups() {
      return this.groups.filter(
        (group) =>
          !this.associations.some(
            (ass) =>
              ass.stock_group_id === group.id &&
              ass.stock_item_category_id === this.stock_item_category_id
          )
      );
    },

    available_categories() {
      return this.categories.filter(
        (category) =>
          !this.associations.some(
            (ass) =>
              ass.stock_group_id === this.stock_group_id &&
              ass.stock_item_category_id === category.id
          )
      );
    },
  },

  methods: {
    getCategoryIcon(typeName) {
      const name = typeName.toLowerCase();
      if (name.includes("medicamento") || name.includes("medicine"))
        return "fas fa-pills";
      if (name.includes("produto") || name.includes("product"))
        return "fas fa-box";
      return "fas fa-tag";
    },

    getGroupName(id) {
      const group = this.groups.find((item) => item.id === id);
      return group ? group.name : "";
    },

    getCategoryName(id) {
      const category = this.categories.find((item) => item.id === id);
      return category ? category.name : "";
    },

    getCategoryTypeName(id) {
      const category = this.categories.find((item) => item.id === id);
      return category ? category.stock_item_type_name : "";
    },

    async evOnShow() {
      this.errors = [];
      this.loading = false;

      this.stock_group_id = "";
      this.stock_item_category_id = "";
    },

    async evOnShown() {
      this.$refs.autofocus.focus();
    },

    async evOnHide() {},

    async evOnSubmit() {
      try {
        this.loading = true;

        const data = {
          stock_group_id: this.stock_group_id,
          stock_item_category_id: this.stock_item_category_id,
        };
        const response = await api.post(
          "/rest-home/stock/group/associate-category",
          data
        );
        const { error, errors, message } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          toast(message, "success");
          this.$bvModal.hide("new_association");
          this.$emit("created");
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Modal Title */
.modal-title-modern {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.modal-title-modern i {
  color: #0f65b0;
}

/* Modal Content */
.modal-content-modern {
  padding: 8px 0;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modern-select {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
}

.modern-select:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

.form-help {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

.form-help i {
  color: #9ca3af;
}

/* Preview Section */
.preview-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #7dd3fc;
  margin-bottom: 24px;
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
}

.preview-title i {
  color: #0284c7;
}

.preview-content {
  background: white;
  border-radius: 8px;
  padding: 16px;
}

.association-preview {
  display: flex;
  align-items: center;
  gap: 16px;
}

.preview-group,
.preview-category {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  flex: 1;
}

.preview-icon {
  color: #0f65b0;
  font-size: 18px;
  width: 20px;
}

.preview-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.preview-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.preview-type {
  font-size: 11px;
  color: #0f65b0;
  font-weight: 500;
}

.association-arrow {
  color: #0f65b0;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 50%;
  border: 2px solid #0f65b0;
}

/* Rules Section */
.rules-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.rules-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
}

.rules-title i {
  color: #f59e0b;
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rule-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.rule-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  flex-shrink: 0;
}

.rule-icon.restriction {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.rule-icon.unique {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.rule-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rule-content strong {
  font-size: 13px;
  color: #1e293b;
  font-weight: 600;
}

.rule-content span {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

/* Form Footer */
.form-footer {
  border-top: 1px solid #e5e7eb;
  padding-top: 20px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.btn-save-modern {
  background: linear-gradient(135deg, #1e884d 0%, #16a34a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(30, 136, 77, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-save-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(30, 136, 77, 0.4) !important;
}

.btn-save-modern:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .association-preview {
    flex-direction: column;
    gap: 12px;
  }

  .association-arrow {
    transform: rotate(90deg);
  }

  .preview-group,
  .preview-category {
    flex: none;
    width: 100%;
  }

  .preview-section,
  .rules-section {
    padding: 16px;
  }

  .modal-content-modern {
    padding: 0;
  }
}

@media (max-width: 480px) {
  .modal-title-modern {
    font-size: 18px;
  }

  .preview-group,
  .preview-category {
    padding: 10px 12px;
  }

  .rule-item {
    padding: 10px;
  }
}
</style>

<!-- Global Modal Styles -->
<style>
.modern-modal >>> .modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-radius: 0 !important;
}

.modern-modal >>> .modal-body {
  padding: 24px !important;
}

.modern-modal >>> .modal-content {
  border: none !important;
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

.modern-modal >>> .modern-select option {
  padding: 8px 12px !important;
  font-size: 14px !important;
}
</style>